import { api, type AppInfo, type HubAccountWithBindings } from "@/lib/api";
import { connectReturnTo, rememberConnecting } from "@/lib/connect-return";
import { useRouterState } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { toast } from "sonner";

// Non-component pieces of the integrations browser, shared by HubApps (Boo
// tab + org settings), the manage page and the onboarding catalog. Lives
// outside HubApps.tsx because react-refresh wants component files to export
// only components.

// How many connected integrations we encourage before a Boo starts paying
// off — the single tool alone is table stakes, the value is stitching a few
// together. Drives the onboarding pitch/nudge and the Integrations tab's
// "connect more" copy, so it lives here as the one source of truth.
export const CONNECT_TARGET = 3;

// Under the Custom view's heading, on the Boo tab.
export const CONNECTORS_SUBCOPY =
  "Integrations Boo built for tools that aren’t in the catalog. Each version runs only after a person approves it here, or merges it on GitHub or GitLab.";

// slug → connected-account count, so catalog tiles can show what's already in.
export function connectedByApp(accounts: HubAccountWithBindings[]): Map<string, number> {
  const counts = new Map<string, number>();
  for (const a of accounts) counts.set(a.app, (counts.get(a.app) ?? 0) + 1);
  return counts;
}

/**
 * Connect flow shared by every catalog surface (Boo tab, org settings, manage
 * page, onboarding). One click asks boo-backend for a one-time connect link
 * and leaves for it: the hub's hosted page runs the consent, boo-backend
 * records the account for the link's scope — private to `containerId`, or a
 * workspace account when absent — binds it there, and sends the browser back
 * to the page it left (`returnTo`, default: this one) with
 * ?integrations=<outcome>, which useConnectReturn turns into a toast and a
 * refetch. `connecting` stays set until the page unloads; it clears if the
 * link could not be minted, or if the browser brings this page back from the
 * back-forward cache (Back from the hub page) with the old state intact.
 */
export function useConnectApp({
  containerId,
  returnTo,
}: {
  containerId?: string;
  returnTo?: string;
}) {
  const location = useRouterState({ select: (s) => s.location });
  const [connecting, setConnecting] = useState<string | null>(null);

  useEffect(() => {
    const onPageShow = (e: PageTransitionEvent) => {
      if (e.persisted) setConnecting(null);
    };
    window.addEventListener("pageshow", onPageShow);
    return () => window.removeEventListener("pageshow", onPageShow);
  }, []);

  const connect = async (app: AppInfo) => {
    if (connecting) return;
    setConnecting(app.slug);
    try {
      const { url } = await api.integrations.connectLink({
        app: app.slug,
        containerId: containerId ?? null,
        returnTo: returnTo ?? connectReturnTo(location.pathname, location.searchStr),
      });
      rememberConnecting(app.name);
      window.location.assign(url);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Could not start the connection");
      setConnecting(null);
    }
  };

  return { connect, connecting };
}
